import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { addAutomationEventFs, getLeadByIdFs } from '../../utils/firestoreStore';
import { createPayFastCheckout, redirectToPayFast, waitForPayFastPayment } from '../../utils/payfastHostedCheckout';
import { useAuth } from '../../context/AuthContext';

const PUBLIC_CHECKOUT_STORAGE_KEY = 'shem_public_registration_checkout';

export default function Pay() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, updateProfile } = useAuth();
  const [lead, setLead] = useState(null);
  const [checkoutData, setCheckoutData] = useState(null);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState('');

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const leadId = useMemo(() => params.get('leadId') || '', [params]);
  const paymentStatus = useMemo(() => params.get('payment') || '', [params]);
  const checkoutMode = useMemo(() => params.get('checkout') || '', [params]);
  const orderId = useMemo(() => params.get('orderId') || '', [params]);

  useEffect(() => {
    const load = async () => {
      if (!leadId) return;
      try {
        const found = await getLeadByIdFs(leadId);
        setLead(found);
      } catch {
        setLead(null);
      }
    };
    load();
  }, [leadId]);

  useEffect(() => {
    if (leadId) return;
    try {
      const stored = JSON.parse(localStorage.getItem(PUBLIC_CHECKOUT_STORAGE_KEY) || 'null');
      setCheckoutData(stored);
    } catch {
      setCheckoutData(null);
    }
  }, [leadId]);

  const amountDue = useMemo(() => {
    if (typeof checkoutData?.amountDue === 'number' && checkoutData.amountDue > 0) return checkoutData.amountDue;
    if (typeof lead?.amountDue === 'number' && lead.amountDue > 0) return lead.amountDue;
    const courseTitle = `${checkoutData?.courseTitle || lead?.courseTitle || ''} ${checkoutData?.intake || lead?.intake || ''}`.toLowerCase();
    if (courseTitle.includes('millionaires connect')) return 2500;
    return 0;
  }, [checkoutData?.amountDue, checkoutData?.courseTitle, checkoutData?.intake, lead?.amountDue, lead?.courseTitle, lead?.intake]);

  const successFlagKey = useMemo(() => {
    if (leadId) return `shem_public_payfast_success_${leadId}`;
    if (checkoutData?.courseId && checkoutData?.email) {
      return `shem_public_payfast_success_${checkoutData.courseId}_${checkoutData.email}`;
    }
    return 'shem_public_payfast_success';
  }, [checkoutData?.courseId, checkoutData?.email, leadId]);

  const payer = useMemo(() => {
    return {
      firstName: checkoutData?.fullName || lead?.fullName || user?.firstName || '',
      lastName: checkoutData?.surname || lead?.surname || user?.lastName || '',
      email: checkoutData?.email || lead?.email || user?.email || '',
      phone: checkoutData?.phone || lead?.phone || user?.phone || ''
    };
  }, [
    checkoutData?.fullName,
    checkoutData?.surname,
    checkoutData?.email,
    checkoutData?.phone,
    lead?.fullName,
    lead?.surname,
    lead?.email,
    lead?.phone,
    user?.firstName,
    user?.lastName,
    user?.email,
    user?.phone
  ]);

  const itemName = useMemo(
    () => lead?.courseTitle || checkoutData?.courseTitle || 'School of Shem Course',
    [lead?.courseTitle, checkoutData?.courseTitle]
  );
  const itemDescription = useMemo(
    () => lead?.intake || checkoutData?.intake || 'Student registration checkout',
    [lead?.intake, checkoutData?.intake]
  );

  const finalizePaymentSuccess = useCallback(async () => {
    updateProfile({
      lmsActive: true,
      pendingApproval: false,
      paymentDue: false,
      accessStatus: 'paid',
      courseId: lead?.courseId || checkoutData?.courseId || user?.courseId || null,
      intake: lead?.intake || checkoutData?.intake || user?.intake || ''
    });

    localStorage.removeItem(PUBLIC_CHECKOUT_STORAGE_KEY);
    setPaymentComplete(true);
    if (leadId) {
      await addAutomationEventFs({
        id: `auto-${Date.now()}`,
        leadId,
        leadName: lead ? `${lead.fullName} ${lead.surname}` : 'Lead',
        template: 'Payment confirmed by PayFast ITN',
        channel: 'Email',
        timestamp: new Date().toISOString()
      });
    }
  }, [checkoutData?.courseId, checkoutData?.intake, lead, leadId, updateProfile, user?.courseId, user?.intake]);

  useEffect(() => {
    if (paymentStatus !== 'success') return;
    if (!orderId) {
      setError('Missing order reference. Please contact support if you were charged.');
      return;
    }
    if (sessionStorage.getItem(successFlagKey)) {
      setPaymentComplete(true);
      return;
    }
    waitForPayFastPayment(orderId)
      .then(() => {
        sessionStorage.setItem(successFlagKey, '1');
        return finalizePaymentSuccess();
      })
      .catch(() => {
        setError('Payment is still being confirmed by PayFast. Please refresh shortly.');
      });
  }, [finalizePaymentSuccess, orderId, paymentStatus, successFlagKey]);

  useEffect(() => {
    if (paymentStatus !== 'cancel') return;
    setError('Your Payfast checkout was cancelled. You can try again below.');
  }, [paymentStatus]);

  const handleStartPayfastCheckout = async () => {
    setError('');
    if (!amountDue) {
      setError('Amount unavailable. Please go back and select a course again.');
      return;
    }

    setCheckoutLoading(true);
    try {
      const sharedParams = new URLSearchParams();
      if (leadId) sharedParams.set('leadId', leadId);
      if (checkoutMode) sharedParams.set('checkout', checkoutMode);
      const generatedOrderId = `public-${leadId || Date.now()}`;
      sharedParams.set('orderId', generatedOrderId);
      const queryPrefix = sharedParams.toString();

      const checkout = await createPayFastCheckout({
        amount: amountDue,
        itemName,
        itemDescription,
        successPath: `/pay?${queryPrefix ? `${queryPrefix}&` : ''}payment=success`,
        cancelPath: `/pay?${queryPrefix ? `${queryPrefix}&` : ''}payment=cancel`,
        metadata: {
          orderId: generatedOrderId,
          type: 'public_registration',
          source: checkoutMode || 'public_pay_page',
          leadId: leadId || null,
          userId: user?.id || null,
          courseId: lead?.courseId || checkoutData?.courseId || null,
          intake: lead?.intake || checkoutData?.intake || '',
          email: payer.email || ''
        },
        customer: payer
      });

      redirectToPayFast(checkout);
    } catch {
      setError('Unable to start Payfast checkout. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (paymentComplete) {
    return (
      <div className="page-content" style={{ maxWidth: '720px', margin: '0 auto' }}>
        <div className="card" style={{ marginTop: '40px' }}>
          <div className="card-body" style={{ textAlign: 'center', padding: '48px' }}>
            <h1 style={{ fontSize: '1.75rem', marginBottom: '12px' }}>Payment received</h1>
            <p style={{ color: 'var(--gray-500)', marginBottom: '24px' }}>
              Your payment has been captured successfully. Your portal access is now active.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button className="btn btn-primary" onClick={() => navigate('/lms?welcome=1', { replace: true })}>
                Open My Portal
              </button>
              <button className="btn btn-outline" onClick={() => navigate('/login')}>
                Go to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content" style={{ maxWidth: '720px', margin: '0 auto' }}>
      <div className="page-header">
        <h1 className="page-title">Payment</h1>
        <p className="page-subtitle">Complete your Payfast payment to unlock your portal immediately.</p>
      </div>

      <div className="card">
        <div className="card-body" style={{ display: 'grid', gap: '16px' }}>
          {error && (
            <div className="badge badge-danger">
              {error}
            </div>
          )}

          <div>
            <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase' }}>Lead</div>
            <div style={{ fontWeight: 600 }}>
              {lead
                ? `${lead.fullName} ${lead.surname}`
                : checkoutData
                  ? `${checkoutData.fullName} ${checkoutData.surname}`
                  : 'Unknown lead'}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase' }}>Course</div>
            <div>{lead?.intake || checkoutData?.intake || 'Select an intake via /register'}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase' }}>Amount</div>
            <div style={{ fontWeight: 600 }}>{amountDue ? `R${amountDue.toLocaleString('en-ZA')}` : 'Amount unavailable'}</div>
          </div>

          <div>
            <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: '8px' }}>
              Payment Method
            </div>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <span className="badge badge-primary">Payfast</span>
            </div>
          </div>

          <button className="btn btn-primary" onClick={handleStartPayfastCheckout} disabled={checkoutLoading || (!leadId && !checkoutData)}>
            {checkoutLoading ? 'Redirecting to Payfast...' : 'Pay with Payfast'}
          </button>

          {!leadId && !checkoutData && (
            <div className="badge badge-warning">
              Missing checkout details. Please register first.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
